import { Card, CardContent, CardHeader, Button, DialogActions, Dialog, Skeleton, DialogTitle, ToggleButton, ToggleButtonGroup, IconButton, Tooltip } from '@mui/material';
import StackedLineChartIcon from '@mui/icons-material/StackedLineChart';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { dashboardApi } from '../../../api/dashboardApi';
import { useTheme } from '@mui/material/styles';
import useMounted from '../../../hooks/useMounted';
import useAuth from '../../../hooks/useAuth';
import Chart from 'react-apexcharts';
import moment from 'moment';
import { getTone, grey, lightVeil, darkVeil } from '../../../theme/tones';
import 'moment/locale/fr';

const FinanceSalesRevenue = () => {
  const theme = useTheme();
  const { user } = useAuth();
  const mounted = useMounted();
  moment.locale('fr');
  
  const [loading, setLoading] = useState(true);
  const [souscriptionsNumberMonth, setSouscriptionsNumberMonth] = useState({});
  const [customersweek, setCustomersweek] = useState([]);
  const [openSouscriptions, setOpenSouscriptions] = useState(false);
  const [souscriptionsNumberDay, setSouscriptionsNumberDay] = useState([]);
  const [groupBy, setGroupBy] = useState('inscription');
  const [chartSeries, setChartSeries] = useState([]);

  // Stable Categories: Last 12 months (always the same regardless of filter)
  const categories = useMemo(() => {
    return Array.from({ length: 12 }, (_, i) =>
      moment().subtract(11 - i, 'months').format('MMM')
    );
  }, []);

  const chartOptions = useMemo(() => ({
    chart: {
      background: 'transparent',
      stacked: false,
      toolbar: {
        show: true
      },
      animations: {
        enabled: true,
        easing: 'easeinout',
        speed: 800
      }
    },
    colors: [getTone('sky').base, getTone('brandGreen').base, getTone('amber').light, getTone('rose').base, getTone('teal').color, getTone('violet').deep],
    dataLabels: {
      enabled: false
    },
    grid: {
      borderColor: theme.palette.divider
    },
    markers: {
      strokeColors: theme.palette.background.paper,
      size: 6,
      hover: {
        size: 8
      }
    },
    stroke: {
      show: true,
      curve: 'smooth',
      width: 3
    },
    fill: {
      type: 'gradient',
      gradient: {
        shadeIntensity: 1,
        opacityFrom: 0.4,
        opacityTo: 0.1,
        stops: [0, 90, 100]
      }
    },
    theme: {
      mode: theme.palette.mode
    },
    xaxis: {
      type: 'category',
      categories: categories,
      axisBorder: {
        color: theme.palette.divider,
        show: true
      },
      axisTicks: {
        color: theme.palette.divider,
        show: true
      },
      crosshairs: {
        show: true,
        width: 1,
        stroke: {
          color: theme.palette.divider,
          width: 1,
          dashArray: 3
        }
      },
      labels: {
        style: {
          colors: theme.palette.text.secondary
        }
      }
    },
    yaxis: {
      labels: {
        formatter: (val) => (typeof val === 'number' ? val.toLocaleString('fr-FR', { maximumFractionDigits: 0 }) : val),
        style: {
          colors: theme.palette.text.secondary
        }
      }
    },
    tooltip: {
      shared: true,
      intersect: false,
      theme: theme.palette.mode,
      custom: function({ series, dataPointIndex, w }) {
        const month = w.globals.categoryLabels[dataPointIndex];
        let html = '<div style="font-family: inherit; font-size: 13px;">';
        html += '<div style="padding: 10px 14px; font-weight: 700; border-bottom: 1px solid ' + theme.palette.divider + '; background: ' + (theme.palette.mode === "dark" ? lightVeil(0.03) : grey(50)) + '; color: ' + theme.palette.text.primary + ';">' + month + '</div>';
        html += '<div style="padding: 10px 14px;">';
        
        w.globals.seriesNames.forEach((name, i) => {
          const val = series[i][dataPointIndex] || 0;
          const color = w.globals.colors[i];
          const formatted = Number(val).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
          html += '<div style="display: flex; justify-content: space-between; align-items: center; padding: 6px 0; gap: 24px;">';
          html += '<div style="display: flex; align-items: center;">';
          html += '<span style="width: 8px; height: 8px; border-radius: 50%; background-color: ' + color + '; margin-right: 8px; flex-shrink: 0; box-shadow: 0 0 6px ' + color + ';"></span>';
          html += '<span style="color: ' + theme.palette.text.secondary + '; font-weight: 500;">' + name + '</span>';
          html += '</div>';
          html += '<span style="font-weight: 700; color: ' + theme.palette.text.primary + ';">' + formatted + '</span>';
          html += '</div>';
        });
        
        html += '</div></div>';
        return '<div style="box-shadow: 0 12px 30px -10px ' + darkVeil(0.15) + '; border-radius: 12px; overflow: hidden; background: ' + theme.palette.background.paper + '; border: 1px solid ' + theme.palette.divider + ';">' + html + '</div>';
      }
    },
    legend: {
        position: 'bottom',
        horizontalAlign: 'center',
        labels: {
            colors: theme.palette.text.primary
        }
    }
  }), [categories, theme.palette.mode, theme.palette.divider, theme.palette.background.paper, theme.palette.text.primary, theme.palette.text.secondary]);

  const getSalesRevenue = useCallback(async () => {
    try {
      setLoading(true);
      const data = await dashboardApi.getSalesRevenue(localStorage.getItem('accessToken'), groupBy);

      if (mounted.current) {
        if (data) {
          setSouscriptionsNumberMonth(data.souscriptionsNumberMonth || {});
          setSouscriptionsNumberDay(data.souscriptionsNumberDay ? data.souscriptionsNumberDay[0] : []);
          setCustomersweek(data.souscriptionsNumberWeek ? data.souscriptionsNumberWeek[0] : []);
        }
        setLoading(false);
      }
    } catch (err) {
      console.error(err);
      if (mounted.current) {
        setLoading(false);
      }
    }
  }, [mounted, groupBy]);

  useEffect(() => {
    getSalesRevenue();
  }, [getSalesRevenue, groupBy]);


  useEffect(() => {
    if (souscriptionsNumberMonth && Object.keys(souscriptionsNumberMonth).length > 0) {
      const list = ['Inscrit', 'InscritD', 'À décaler', 'Annulation', 'Chiffre d\'affaire', 'Terminés'];
      const newChartSeries = list.map((ls) => ({
        name: ls,
        data: (souscriptionsNumberMonth[ls] || []).map(val => {
          const num = Number(val);
          return isNaN(num) ? 0 : Math.round(num * 100) / 100;
        }),
      }));
      setChartSeries(newChartSeries);
    }
  }, [souscriptionsNumberMonth]);

  // Handle Dialog charts (Days & Weeks)
  const chartSeriesDays = useMemo(() => ([
    { name: 'Nouveaux Apprenants',
      data: [souscriptionsNumberDay.numberday1, souscriptionsNumberDay.numberday2, souscriptionsNumberDay.numberday3, souscriptionsNumberDay.numberday4, souscriptionsNumberDay.numberday5,
        souscriptionsNumberDay.numberday6] 
    }
  ]), [souscriptionsNumberDay]);

  const chartOptionsDays = useMemo(() => ({
    chart: { background: 'transparent', toolbar: { show: false } },
    colors: [getTone('brandGreen').base],
    xaxis: {
      categories: Array.from({ length: 7 }, (_, i) => 
        new Date(Date.now() - 864e5 * (6 - i)).toLocaleDateString('fr-FR', { weekday: 'long' })
      )
    },
    theme: { mode: theme.palette.mode }
  }), [theme.palette.mode]);

  const chartSeriesWeek = useMemo(() => ([
    { name: 'Nouveaux Apprenants', data: [customersweek.numberweek1, customersweek.numberweek2, customersweek.numberweek3, customersweek.numberweek4] }
  ]), [customersweek]);

  const chartOptionsWeek = useMemo(() => ({
    chart: { background: 'transparent', toolbar: { show: false } },
    colors: [getTone('brandGreen').base],
    xaxis: {
      categories: ['Semaine 4', 'Semaine 3', 'Semaine 2', 'Semaine 1']
    },
    theme: { mode: theme.palette.mode }
  }), [theme.palette.mode]);

  return (
    <Card
      sx={{
        borderRadius: '16px',
        border: (theme) => `1px solid ${theme.palette.divider}`,
        boxShadow: (theme) => theme.palette.mode === 'light' 
          ? `0 4px 18px 0 ${darkVeil(0.03)}` 
          : `0 4px 18px 0 ${darkVeil(0.2)}`,
        height: '100%'
      }}
    >
      {!loading ? (
        <CardHeader
          title={user.access_level === 0 ? (groupBy === 'session' ? 'Chiffres par date de fin de session' : 'Chiffres par date d\'inscription') : user.id_service === 3 ? 'Nombre d\'inscriptions par mois' : 'Progression moyenne par session'}
          action={user.access_level === 0 && (
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <ToggleButtonGroup
                value={groupBy}
                exclusive
                onChange={(e, value) => value && setGroupBy(value)}
                size="small"
                sx={{
                  mr: 2,
                  backgroundColor: (theme) => theme.palette.mode === 'light' ? darkVeil(0.03) : lightVeil(0.05),
                  borderRadius: '12px',
                  p: '3px',
                  border: 'none',
                  '& .MuiToggleButtonGroup-grouped': {
                    border: 0,
                    borderRadius: '8px',
                    px: 1.5,
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    textTransform: 'capitalize',
                    '&.Mui-selected': {
                      backgroundColor: 'primary.main',
                      color: 'primary.contrastText',
                      boxShadow: `0 2px 8px ${darkVeil(0.15)}`,
                      '&:hover': {
                        backgroundColor: 'primary.main',
                      }
                    },
                    '&:not(.Mui-selected)': {
                      color: 'text.secondary',
                    }
                  }
                }}
              >
                <ToggleButton value="inscription">Inscription</ToggleButton>
                <ToggleButton value="session">Session</ToggleButton>
              </ToggleButtonGroup>
              <Tooltip title="Détails par jour / semaine">
                <IconButton
                  onClick={() => setOpenSouscriptions(true)}
                  color="primary"
                  sx={{
                    border: (theme) => `1px solid ${theme.palette.divider}`,
                    borderRadius: '10px',
                    p: '6px'
                  }}
                >
                  <StackedLineChartIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </div>
          )}
        />
      ) : (
        <Skeleton variant="text" sx={{ ml: 4 }} animation="wave" width={150} height={40} />
      )}
      
      <Dialog fullScreen open={openSouscriptions} onClose={() => setOpenSouscriptions(false)}>
        <DialogTitle>Par Jour</DialogTitle>
        <DialogActions>
          <Button onClick={() => setOpenSouscriptions(false)} color="primary">Fermer</Button>
        </DialogActions>
        <CardContent>
          <Chart height="400" options={chartOptionsDays} series={chartSeriesDays} type="line" />
        </CardContent>
        <DialogTitle>Par Semaine</DialogTitle>
        <CardContent>
          <Chart height="400" options={chartOptionsWeek} series={chartSeriesWeek} type="line" />
        </CardContent>
      </Dialog>

      {user.id_service !== 4 && (
        <CardContent>
          {!loading ? (
            <Chart
              height="400"
              options={chartOptions}
              series={chartSeries}
              type="area"
            />
          ) : (
            <Skeleton variant="rectangular" animation="wave" height={400} />
          )}
        </CardContent>
      )}
    </Card>
  );
};

export default FinanceSalesRevenue;
